/**
 * Partners dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PartnersDataTable } from '@/components/partners/PartnersDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreatePartnerView } from '@/components/partners/CreatePartnerView';
import { EditPartnerView } from '@/components/partners/EditPartnerView';
import { usePartnerPageActions } from '@/components/partners/partner-table-utils';
import { useDetailsView } from '@/hooks/useDetailsView';
import { partnersKeys } from '@/hooks/usePartners';
import type { Partner } from '@/types/partner.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Dashboard partners widget: optional layout width from the IE config. */
interface PartnersSectionProps {
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: partners list. */
export const PartnersSection: React.FC<PartnersSectionProps> = ({ layoutWidth = 'full' }) => {
  const partnerActions = usePartnerPageActions();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingPartnerId, setEditingPartnerId] = React.useState<string | null>(null);

  const handleView = (partner: Partner) => {
    openRow(partner.id);
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartnerId(partner.id);
    closeRow();
  };

  const handleOpenCreate = () => {
    setCreateOpen(true);
    closeRow();
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleCloseEdit = () => {
    setEditingPartnerId(null);
  };

  const handleSuccess = () => {
    setCreateOpen(false);
    setEditingPartnerId(null);
    void queryClient.invalidateQueries({ queryKey: partnersKeys.all });
  };

  return (
    <>
      <PartnersDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        pageActions={{
          canView: partnerActions.canViewPartner,
          canEdit: partnerActions.canEditPartner,
          canDelete: partnerActions.canDeletePartner,
          canExportExcel: partnerActions.canExportPartners,
        }}
        onView={handleView}
        onEdit={handleEdit}
        isSection={true}
        layoutWidth={layoutWidth}
        showCreateButton={partnerActions.canCreatePartner}
        onCreate={handleOpenCreate}
      />

      <SlidePanel isOpen={createOpen} onClose={handleCloseCreate}>
        <CreatePartnerView onClose={handleCloseCreate} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editingPartnerId !== null} onClose={handleCloseEdit}>
        {editingPartnerId ? (
          <EditPartnerView
            key={editingPartnerId}
            partnerId={editingPartnerId}
            onClose={handleCloseEdit}
            onSuccess={handleSuccess}
          />
        ) : null}
      </SlidePanel>
    </>
  );
};
